import { cn } from '@/utils/ui/styles'

type LogoProps = {
	className?: string
}

interface LogoWithTextProps extends LogoProps {
	text?: boolean
}

/** The standalone Bitlyft mark (no wordmark). */
export const Logo = ({ className }: LogoProps) => {
	return (
		<svg
			viewBox="0 0 30 43"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
		>
			<g id="Group 27">
				<path
					id="Vector 6"
					d="M19.7571 5.6989L10.2441 0.466797V20.5871L19.7571 25.8192V5.6989Z"
					fill="#CEE2FF"
				/>
				<path
					id="Vector 7"
					d="M29.2688 20.1115L19.7559 25.8192V15.1172L29.2688 20.1115Z"
					fill="url(#paint0_linear_24495_4940)"
				/>
				<path
					id="Vector 8"
					d="M29.2688 20.1113V31.0512L19.7559 25.8191L29.2688 20.1113Z"
					fill="#2261DB"
				/>
				<path
					id="Union"
					d="M29.2692 31.0505L19.7563 25.8184L0.730469 37.2339L10.2434 42.466L29.2692 31.0505Z"
					fill="url(#paint1_linear_24495_4940)"
				/>
			</g>
			<defs>
				<linearGradient
					id="paint0_linear_24495_4940"
					x1="26.893"
					y1="15.3818"
					x2="19.9782"
					y2="25.8923"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#91BDFF" />
					<stop offset="1" stopColor="#CEE2FF" />
				</linearGradient>
				<linearGradient
					id="paint1_linear_24495_4940"
					x1="24.9572"
					y1="26.7212"
					x2="10.5744"
					y2="44.4231"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#3F78E7" />
					<stop offset="1" stopColor="#CEE2FF" />
				</linearGradient>
			</defs>
		</svg>
	)
}

/**
 * Bitlyft logo lockup. With `text=true` (default) renders the mark + wordmark;
 * `text=false` renders the mark alone.
 */
export const LogoWithText = ({ className, text = true }: LogoWithTextProps) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox={text ? '0 0 84 35' : '32 0 84 35'}
			className={cn('shrink-0 grow-0', className)}
		>
			<path fill="#CEE2FF" d="M70.727 4.36 62.953 0v16.767l7.774 4.36V4.36Z" />
			<path fill="url(#a)" d="m78.5 16.37-7.773 4.757V12.21l7.774 4.162Z" />
			<path fill="#2261DB" d="M78.5 16.37v9.117l-7.773-4.36L78.5 16.37Z" />
			<path
				fill="url(#b)"
				d="m78.502 25.488-7.774-4.36L55.18 30.64 62.954 35l15.548-9.513Z"
			/>
			{text && (
				<g>
					<path
						fill="currentColor"
						d="M45.902 10.264c-1.222 0-2.185.247-2.811.811l-.003.003c-.617.568-.892 1.427-.892 2.5v.33h-3.391l-2.925 7.476-3.023-7.477h-2.066l4.053 9.703-1.642 3.978h1.99l4.906-12.007h2.098v8.407h1.887v-8.407h2.824v-1.674h-2.824v-.328c0-.665.15-1.056.368-1.264.233-.212.685-.36 1.451-.36h.332v-1.69h-.332Zm-28.438.234h-1.957v1.988h1.957v-1.988Z"
					/>
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M9.99 11.266H5.549v12.722h4.563c.864 0 1.626-.144 2.274-.445l.005-.002c.642-.31 1.142-.735 1.482-1.278a3.35 3.35 0 0 0 .522-1.83c0-.824-.265-1.555-.794-2.179a3.408 3.408 0 0 0-1.072-.858c.338-.19.636-.429.893-.719l.002-.003c.47-.543.692-1.233.692-2.042 0-.987-.364-1.809-1.092-2.438-.723-.636-1.753-.928-3.034-.928ZM7.436 16.6v-3.66h2.466c.82 0 1.381.18 1.741.489l.003.002c.37.31.565.736.565 1.322 0 .599-.196 1.038-.568 1.358-.362.31-.908.49-1.688.49H7.436Zm0 5.715v-4.058h2.536c.819 0 1.427.203 1.864.576l.006.005c.441.358.666.837.666 1.474 0 .641-.217 1.114-.633 1.458l-.003.002c-.41.348-1.011.543-1.847.543H7.436Z"
						clipRule="evenodd"
					/>
					<path
						fill="currentColor"
						d="M27.31 11.266h-1.888v12.722h6.276V22.35H27.31V11.266Zm-5.778.276h-1.905v2.365h-1.329v1.674h1.33v5.525c0 .948.217 1.71.73 2.203l.003.003c.505.472 1.244.676 2.15.676h1.644v-1.69h-1.45c-.536 0-.812-.108-.938-.232-.124-.123-.235-.404-.235-.96V15.58h2.623v-1.674h-2.623v-2.365Zm28.552 0h-1.905v2.365h-1.33v1.674h1.33v5.525c0 .948.217 1.71.73 2.203l.003.003c.506.472 1.245.676 2.151.676h1.643v-1.69h-1.45c-.535 0-.812-.108-.937-.232-.124-.123-.236-.404-.236-.96V15.58h2.623v-1.674h-2.623v-2.365Zm-32.652 2.365h-1.887v10.081h1.887v-10.08Z"
					/>
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="m43.177 11.175-.001.002c-.58.534-.85 1.349-.85 2.402v.462h-3.432l-3.012 7.698-3.112-7.698h-1.781l3.998 9.569-1.588 3.845h1.705l4.906-12.008h2.317v8.408h1.625v-8.408h2.767v-1.406h-2.767v-.462c0-.68.153-1.116.41-1.362l.002-.002c.27-.246.763-.393 1.538-.393h.2v-1.424h-.2c-1.21 0-2.134.245-2.725.777Zm4.87 2.599v-2.366h2.168v2.366h2.622v1.94h-2.623v5.392c0 .547.112.78.196.864.087.086.32.194.846.194h1.581v1.958h-1.774c-.922 0-1.7-.208-2.24-.711v-.001l-.003-.003-.001-.001c-.55-.528-.771-1.335-.771-2.3v-5.392h-3.834v8.408h-2.15v-8.408h-1.879l-4.906 12.008h-2.275l1.698-4.112-4.11-9.836h2.353l2.933 7.255 2.838-7.255h3.349v-.195c0-1.096.28-1.997.935-2.6v-.001l.003-.002c.662-.597 1.665-.845 2.9-.845h.462v1.958h-.463c-.755 0-1.166.146-1.362.324-.179.172-.326.518-.326 1.166v.195h3.834Zm-1.01 1.673h1.273v5.659c0 .931.214 1.65.688 2.105l.003.002c.471.44 1.172.642 2.062.642h1.512V22.43h-1.32c-.543 0-.864-.108-1.028-.27-.163-.161-.274-.49-.274-1.055v-5.659h2.622V14.04h-2.623v-2.365H48.31v2.365h-1.272v1.406Zm-31.661-5.082h2.219v2.254h-2.22v-2.254Zm.262.267v1.72h1.695v-1.72h-1.695Zm-10.22.5H9.99c1.298 0 2.363.296 3.12.96.757.656 1.137 1.515 1.137 2.54 0 .835-.23 1.559-.724 2.13l-.003.004h-.001a3.53 3.53 0 0 1-.724.625c.335.199.637.459.905.776.55.648.826 1.41.826 2.265 0 .694-.18 1.332-.543 1.904-.356.567-.876 1.006-1.536 1.325l-.006.003c-.67.312-1.45.458-2.329.458H5.418v-12.99Zm.262.267v12.456h4.432c.85 0 1.592-.142 2.22-.433l.003-.002c.623-.302 1.102-.71 1.428-1.229a3.213 3.213 0 0 0 .501-1.758 3.14 3.14 0 0 0-.762-2.091 3.278 3.278 0 0 0-1.03-.826l-.226-.113.22-.124a3.3 3.3 0 0 0 .858-.69v-.001l.002-.002c.444-.514.659-1.171.659-1.954 0-.949-.348-1.733-1.045-2.336l-.001-.001c-.691-.607-1.685-.896-2.95-.896H5.68Zm19.611-.267h2.15v11.084h4.388v1.906h-6.538v-12.99Zm.262.267v12.456h6.014v-1.372h-4.389V11.399h-1.625Zm-6.057.01h2.167v2.365h2.623v1.94h-2.623v5.392c0 .547.111.78.195.864.088.086.32.194.846.194h1.582v1.958h-1.774c-.923 0-1.7-.208-2.24-.711l-.005-.005c-.55-.528-.77-1.335-.77-2.3v-5.392h-1.33v-1.94h1.33v-2.366Zm.262.267v2.365H18.43v1.406h1.33v5.659c0 .93.213 1.649.687 2.105l.003.002c.472.44 1.173.642 2.063.642h1.512V22.43h-1.32c-.544 0-.865-.108-1.028-.27-.164-.161-.275-.49-.275-1.055v-5.659h2.623V14.04H21.4v-2.365h-1.643Zm-12.453 1.13h2.597c.836 0 1.432.184 1.825.52l.002.002c.404.337.613.804.613 1.425 0 .633-.21 1.112-.615 1.46-.393.338-.974.521-1.772.521h-2.65v-3.928Zm.262.267v3.394h2.388c.762 0 1.274-.175 1.604-.458.338-.292.521-.692.521-1.256 0-.551-.181-.938-.517-1.218l-.004-.004c-.327-.28-.854-.458-1.657-.458H7.567Zm7.847.7h2.149v10.349h-2.15V13.774Zm.262.268v9.814H17.3V14.04h-1.625Zm-8.37 4.082h2.666c.839 0 1.48.21 1.947.607l.005.004c.474.385.715.903.715 1.578 0 .677-.231 1.189-.68 1.561h-.002l-.001.002c-.442.374-1.076.573-1.931.573h-2.72v-4.325Zm.261.267v3.791h2.458c.817 0 1.384-.19 1.764-.512l.004-.003c.382-.316.584-.748.584-1.354 0-.599-.208-1.038-.616-1.37l-.009-.006c-.407-.347-.983-.546-1.78-.546H7.567Z"
						clipRule="evenodd"
					/>
				</g>
			)}
			<defs>
				<linearGradient
					id="a"
					x1="76.559"
					x2="70.753"
					y1="12.43"
					y2="21.084"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#91BDFF" />
					<stop offset="1" stopColor="#CEE2FF" />
				</linearGradient>
				<linearGradient
					id="b"
					x1="74.978"
					x2="62.946"
					y1="21.88"
					y2="36.402"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#3F78E7" />
					<stop offset="1" stopColor="#CEE2FF" />
				</linearGradient>
			</defs>
		</svg>
	)
}

/** Single-color (`currentColor`) variant of the mark, for dark backgrounds. */
export const LogoWhite = ({ className }: LogoProps) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 22 32"
			className={className}
		>
			<path
				fill="currentColor"
				d="M14.624 3.986 7.376 0v15.33l7.248 3.986V3.986Z"
			/>
			<path
				fill="currentColor"
				d="m21.872 14.967-7.248 4.35v-8.155l7.248 3.805Z"
			/>
			<path
				fill="currentColor"
				d="M21.872 14.967v8.335l-7.248-3.986 7.248-4.349Z"
			/>
			<path
				fill="currentColor"
				d="m21.872 23.302-7.248-3.986L.128 28.014 7.376 32l14.496-8.698Z"
			/>
		</svg>
	)
}
